/**
 * React Query hooks for the investor portal.
 *
 * Polling cadence is gentle by design — investor-facing data doesn't
 * need real-time updates. 60-120s refetch is sufficient and keeps the
 * backend load low even if every shareholder leaves the tab open.
 */
import { useQuery } from '@tanstack/react-query';
import {
  fetchCompanyMetrics,
  fetchMetricsTimeseries,
  fetchMyAnnouncements,
  fetchMyDocuments,
  fetchMyShares,
  fetchMyTransactions,
  fetchNetworkStats
} from '@/api/portal';

export const useMyShares = () =>
  useQuery({
    queryKey: ['myShares'],
    queryFn: fetchMyShares,
    refetchInterval: 60_000
  });

export const useCompanyMetrics = () =>
  useQuery({
    queryKey: ['companyMetrics'],
    queryFn: fetchCompanyMetrics,
    refetchInterval: 60_000
  });

export const useMyTransactions = (limit = 25) =>
  useQuery({
    queryKey: ['myTransactions', limit],
    queryFn: () => fetchMyTransactions({ limit }),
    refetchInterval: 120_000
  });

export const useMyDocuments = () =>
  useQuery({
    queryKey: ['myDocuments'],
    queryFn: () => fetchMyDocuments({}),
    refetchInterval: 300_000
  });

export const useMyAnnouncements = (limit = 10) =>
  useQuery({
    queryKey: ['myAnnouncements', limit],
    queryFn: () => fetchMyAnnouncements({ limit }),
    refetchInterval: 30_000
  });

export const useMetricsTimeseries = (months = 12) =>
  useQuery({
    queryKey: ['metricsTimeseries', months],
    queryFn: () => fetchMetricsTimeseries({ months }),
    refetchInterval: 300_000
  });

export const useNetworkStats = () =>
  useQuery({
    queryKey: ['networkStats'],
    queryFn: fetchNetworkStats,
    refetchInterval: 60_000
  });
